import React, { useState, useRef, useEffect, useContext } from "react";

//Import Mui
import {
  Box,
  Button,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import MuiTextField from "../../controls/MuiTextField";
import MuiAutoComplete from "../../controls/MuiAutoComplete";
import MuiDatePicker from "../../controls/MuiDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Alert from "../../controls/MuiSnackbarAlert";

//Import Redux
import { useDispatch, useSelector } from "react-redux";

//Import React Router Dom
import { useNavigate } from "react-router-dom";

//Import Reduxtoolkit
import { unwrapResult } from "@reduxjs/toolkit";

//Import from customerSlice.js
import {
  getCustomersFromServer,
  selectAllCustomers,
} from "../../masters/customer/customerSlice";

//Import from collectionReportSlice.js
import {
  getCollectionDetailsFromServer,
  getCollectionDetailsFromServerByPagination,
} from "./collectionReportSlice";

import { getCurrentFormattedDate } from "../../../app/constants";

//Import ReactToPrint
import { useReactToPrint } from "react-to-print";

// Import Excel & PDF
import jsPDF from "jspdf";
import "jspdf-autotable";

//Import from Assets
import logo from "../../../assets/MARFish.png";

//Import CSS
import "./collectionReport.css";

const CollectionReport = () => {
  const dispatch = useDispatch();
  const customersList = useSelector(selectAllCustomers);
  const currentDate = getCurrentFormattedDate();

  //useState
  const [selectedFromDate, setSelectedFromDate] = useState(dayjs());
  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [selectedToDate, setSelectedToDate] = useState(dayjs());
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCode, setCustomerCode] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [tableData, setTableData] = useState([]);
  const [originalCollectionsList, setOriginalCollectionsList] = useState([]);
  const [collectionCount, setCollectionCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  //End useState

  //useRef
  const customersRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const searchRef = useRef(null);
  const printRef = useRef(null);
  const refreshRef = useRef(null);
  //End useRef

  const fetchDatasByPagination = async (pageNo, rowSize) => {
    const formatedFromDate = dayjs(selectedFromDate).format("DD/MM/YYYY");
    const formatedToDate = dayjs(selectedToDate).format("DD/MM/YYYY");
    let record = {
      customerCode,
      selectedFromDate: formatedFromDate,
      selectedToDate: formatedToDate,
    };
    try {
      const result = await dispatch(
        getCollectionDetailsFromServerByPagination({ record, pageNo, rowSize })
      );
      const unwrappedResult = unwrapResult(result);
      if (unwrappedResult.data.length === 0) {
        setNotify({
          isOpen: true,
          message: unwrappedResult.message,
          severity: "warning",
        });
      } else {
        setTableData(unwrappedResult.data);
      }
    } catch (err) {
      console.log({ ErrorOccured: err });
      setNotify({
        isOpen: true,
        message: err.message,
        severity: "error",
      });
    }
  };

  //useEffect
  useEffect(() => {
    // if (customersList !== undefined && customersList.length > 0) {
    //   setCustomers(customersList);
    // } else {
    dispatch(getCustomersFromServer())
      .then(unwrapResult)
      .then((result) => {
        if (result.data.length > 0) {
          setCustomers(result.data);
        }
      })
      .catch((err) => {
        console.log({ ErrorOccured: err });
        setNotify({
          isOpen: true,
          message: err.message,
          severity: "error",
        });
      });
    // }
  }, []);

  const handleFromDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      setSelectedFromDate(newValue);
      setIsFromDatePickerOpen(false);
    }
  };

  const handleFromDateBlur = (event) => {
    const inputValue = event.target.value;
    const parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
    if (!parsedDate.isValid()) {
      // This is required
      // setError({
      //   salesDate: "Enter a valid date in DD/MM/YYYY format",
      //   customerName: "",
      //   itemName: "",
      //   quantity: "",
      //   uomName: "",
      //   unitPrice: "",
      // });
      setSelectedFromDate(dayjs());
      setIsFromDatePickerOpen(false);
    } else {
      // This is required
      // setError({ salesDate: "" }); // Clear error if date is valid
      setSelectedFromDate(parsedDate);
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      setSelectedToDate(newValue);
      setIsToDatePickerOpen(false);
    }
  };

  const handleToDateBlur = (event) => {
    const inputValue = event.target.value;
    const parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
    if (!parsedDate.isValid()) {
      // This is required
      // setError({
      //   salesDate: "Enter a valid date in DD/MM/YYYY format",
      //   customerName: "",
      //   itemName: "",
      //   quantity: "",
      //   uomName: "",
      //   unitPrice: "",
      // });
      setSelectedToDate(dayjs());
      setIsToDatePickerOpen(false);
    } else {
      // This is required
      // setError({ salesDate: "" }); // Clear error if date is valid
      setSelectedToDate(parsedDate);
    }
  };

  const handleCustomerChange = async (e, value) => {
    e.preventDefault();
    setSelectedCustomer(value);
    if (value) {
      setCustomerCode(value.CUCode);
      setCustomerName(value.CUName);
      // Remove commas from the OpeningBalance string and convert it to a number
      // const cleanOpeningBalance = parseFloat(
      //   value.OpeningBalance.replace(/,/g, "")
      // );

      fromDateRef.current.focus();
    }
  };

  const columns = [
    { id: "Id", label: "Id", minWidth: 100 },
    { id: "SalesDate", label: "Date", minWidth: 100 },
    {
      id: "OpeningBalance",
      label: "Opening Balance",
      align: "right",
      minWidth: 170,
    },
    {
      id: "CollectionAmount",
      label: "Collection Amount",
      align: "right",
      minWidth: 170,
    },
  ];

  const handleSearch = async (e) => {
    e.preventDefault();
    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else if (selectedFromDate === null) {
      setNotify({
        isOpen: true,
        message: "From Date is required",
        severity: "warning",
      });
      fromDateRef.current.focus();
      return;
    } else if (selectedToDate === null) {
      setNotify({
        isOpen: true,
        message: "To Date is required",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (selectedToDate.isBefore(selectedFromDate)) {
      setNotify({
        isOpen: true,
        message: "To Date should not be earlier than From Date",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else {
      const formatedFromDate = dayjs(selectedFromDate).format("DD/MM/YYYY");
      const formatedToDate = dayjs(selectedToDate).format("DD/MM/YYYY");
      let record = {
        customerCode,
        selectedFromDate: formatedFromDate,
        selectedToDate: formatedToDate,
      };
      await fetchDatasByPagination(page + 1, rowsPerPage);

      // await dispatch(getCollectionsCountFromServer(record))
      //   .then(unwrapResult)
      //   .then((result) => {
      //     setCollectionCount(result.data[0].CollectionsCount);
      //   })
      //   .catch((err) => console.log({ objError: err }));

      await dispatch(getCollectionDetailsFromServer(record))
        .then(unwrapResult)
        .then((result) => {
          if (result.data.length === 0) {
            setCollectionCount(0);
            setOriginalCollectionsList([]);
          } else {
            setCollectionCount(result.data.length);
            setOriginalCollectionsList(result.data);
          }
        })
        .catch((err) => {
          console.log({ ErrorOccured: err });
          setNotify({
            isOpen: true,
            message: err.message,
            severity: "error",
          });
        });
    }
  };

  const handlePrint = async (e) => {
    e.preventDefault();
    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else if (selectedFromDate === null) {
      setNotify({
        isOpen: true,
        message: "From date is required",
        severity: "warning",
      });
      fromDateRef.current.focus();
      return;
    } else if (selectedToDate === null) {
      setNotify({
        isOpen: true,
        message: "To Date is required",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (selectedToDate.isBefore(selectedFromDate)) {
      setNotify({
        isOpen: true,
        message: "To Date should not be earlier than From Date",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (tableData && tableData.length === 0) {
      setNotify({
        isOpen: true,
        message: "Collection details not found, please click on Search",
        severity: "warning",
      });
      return;
    } else {
      const doc = new jsPDF();

      // Load and add the image
      const imgData = logo; // Image path from your assets folder
      doc.addImage(imgData, "PNG", 90, 5, 30, 20); // Adjust the position (x, y) and size (width, height)

      // Add the address details below the image
      const address = `
              MAR & SONS FISH COMPANY
        ShopNo:7, Sandhaipettai Fish Market,
        Thennampalayam, Tirupur-641 604.
        9952420505, 9500951327, 9894777666`;

      doc.setFontSize(10); // Set font size for address text
      doc.text(address, 70, 28); // Adjust position (x, y) as necessary

      const tableColumn = columns.slice(1, 4).map((col) => col.label);

      const tableRows = originalCollectionsList.map((item) =>
        columns.slice(1, 4).map((col) => item[col.id])
      );

      // Find the indices of the 'Opening Balance' and 'Collection Amount' columns
      const openingBalanceIndex = tableColumn.indexOf("Opening Balance");
      const collectionAmountIndex = tableColumn.indexOf("Collection Amount");

      // Add the table below the address
      doc.autoTable({
        startY: 50,
        head: [tableColumn],
        body: tableRows,
        margin: { top: 10 },
        styles: { overflow: "linebreak" },
        headStyles: { fontSize: 10 },
        columnStyles: {
          [openingBalanceIndex]: { halign: "right" }, // Align data cells to the right
          [collectionAmountIndex]: { halign: "right" }, // Align data cells to the right
        },
        didParseCell: function (data) {
          // Check if the cell is a header cell
          if (data.section === "head") {
            if (
              data.column.index === openingBalanceIndex ||
              data.column.index === collectionAmountIndex
            ) {
              data.cell.styles.halign = "right"; // Align specific header cells to the right
            }
          }
        },
      });

      // Save the PDF
      doc.save("CollectionReport_" + currentDate + ".pdf");
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
    fetchDatasByPagination(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
    fetchDatasByPagination(page + 1, event.target.value);
  };

  return (
    <>
      <Alert notify={notify} setNotify={setNotify} />
      <Box className="collectionreportboxcontainer">
        <Paper className="paper" elevation={2} sx={{ p: 2 }}>
          {/* <h6>Add Customer</h6>
            <Divider /> */}
          <CardContent>
            <Grid container spacing={2} className="gridContainer">
              <Grid item xs={12} sm={6} md={3}>
                <MuiAutoComplete
                  id="customer"
                  clearOnEscape
                  autoHighlight
                  autoSelect
                  options={customers}
                  getOptionLabel={(option) =>
                    `${option.CUCode} - ${option.CUName}` || ""
                  }
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Customer *"
                      inputRef={customersRef}
                      autoFocus
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MuiDatePicker
                    label="From Date *"
                    value={selectedFromDate}
                    onChange={handleFromDateChange}
                    open={isFromDatePickerOpen}
                    onClose={() => setIsFromDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        onClick: (e) => {
                          e.stopPropagation();
                          setIsFromDatePickerOpen(true);
                        },
                        onBlur: handleFromDateBlur,
                        inputRef: fromDateRef,
                        onKeyDown: (e) => {
                          if (e.key.toLowerCase() === "home")
                            customersRef.current.focus();
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MuiDatePicker
                    label="To Date *"
                    value={selectedToDate}
                    onChange={handleToDateChange}
                    open={isToDatePickerOpen}
                    onClose={() => setIsToDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        onClick: (e) => {
                          e.stopPropagation();
                          setIsToDatePickerOpen(true);
                        },
                        onBlur: handleToDateBlur,
                        inputRef: toDateRef,
                        onKeyDown: (e) => {
                          if (e.key.toLowerCase() === "home")
                            fromDateRef.current.focus();
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnsearch"
                  variant="contained"
                  size="small"
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      toDateRef.current.focus();
                  }}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  size="small"
                  className="btnprint"
                  ref={printRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      searchRef.current.focus();
                  }}
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnrefresh"
                  variant="contained"
                  size="small"
                  ref={refreshRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      printRef.current.focus();
                  }}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12}></Grid>
              <TableContainer sx={{ maxHeight: 1000 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{ minWidth: column.minWidth }}
                          hidden={column.id === "Id" ? true : false}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData.length > 0 ? (
                      tableData.map((collection) => (
                        <TableRow key={collection.Id}>
                          <TableCell hidden={true}>{collection.Id}</TableCell>
                          <TableCell>{collection.SalesDate}</TableCell>
                          <TableCell align="right">
                            {collection.OpeningBalance}
                          </TableCell>
                          <TableCell align="right">
                            {collection.CollectionAmount}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          No Collections found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                className="MuiTablePagination-selectLabel"
                rowsPerPageOptions={[10, 25, 50, 75, 100]}
                component="div"
                count={collectionCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </CardContent>
        </Paper>
      </Box>
    </>
  );
};

export default CollectionReport;
